import { useCallback, useEffect, useRef } from 'react'

const MAX_ENTRIES = 50
const MAX_AGE_MS = 10 * 60 * 1000 // 10 minutes

/**
 * Rolling activity buffer that records admin actions for bug report context.
 * Captures: page navigations, button clicks, API calls, and console errors.
 *
 * Usage:
 *   const { getActivityLog, getConsoleErrors, getEnvironment } = useActivityTracker()
 */
export default function useActivityTracker() {
  const activityRef = useRef([])
  const consoleErrorsRef = useRef([])

  const addEntry = useCallback((action, details) => {
    const entry = { action, details, timestamp: Date.now() }
    activityRef.current.push(entry)

    // Trim by count
    if (activityRef.current.length > MAX_ENTRIES) {
      activityRef.current = activityRef.current.slice(-MAX_ENTRIES)
    }

    // Trim by age
    const cutoff = Date.now() - MAX_AGE_MS
    activityRef.current = activityRef.current.filter((e) => e.timestamp >= cutoff)
  }, [])

  // Track page navigations
  useEffect(() => {
    addEntry('page_view', window.location.pathname + window.location.search)

    const handlePopState = () => {
      addEntry('navigation', window.location.pathname)
    }
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [addEntry])

  // Track clicks on buttons and interactive elements
  useEffect(() => {
    const handleClick = (e) => {
      const target = e.target.closest('button, a, [role="button"], input[type="submit"]')
      if (!target) return

      const label =
        target.textContent?.trim().substring(0, 60) ||
        target.getAttribute('aria-label') ||
        target.className?.substring(0, 40) ||
        target.tagName

      addEntry('click', label)
    }

    document.addEventListener('click', handleClick, { capture: true })
    return () => document.removeEventListener('click', handleClick, { capture: true })
  }, [addEntry])

  // Intercept fetch/XHR to track API calls
  useEffect(() => {
    const originalFetch = window.fetch
    window.fetch = async (...args) => {
      const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || ''
      const method = args[1]?.method || 'GET'

      if (url.includes('/api/')) {
        addEntry('api_call', `${method} ${url.split('?')[0]}`)
      }

      return originalFetch.apply(window, args)
    }

    return () => {
      window.fetch = originalFetch
    }
  }, [addEntry])

  // Capture console errors
  useEffect(() => {
    const handleError = (event) => {
      const msg = event.message || String(event)
      consoleErrorsRef.current.push({
        message: msg.substring(0, 200),
        source: event.filename || '',
        line: event.lineno || 0,
        timestamp: Date.now(),
      })
      if (consoleErrorsRef.current.length > 10) {
        consoleErrorsRef.current = consoleErrorsRef.current.slice(-10)
      }
    }

    const handleUnhandledRejection = (event) => {
      const msg = event.reason?.message || String(event.reason)
      consoleErrorsRef.current.push({
        message: `Unhandled Promise: ${msg.substring(0, 180)}`,
        source: '',
        line: 0,
        timestamp: Date.now(),
      })
      if (consoleErrorsRef.current.length > 10) {
        consoleErrorsRef.current = consoleErrorsRef.current.slice(-10)
      }
    }

    window.addEventListener('error', handleError)
    window.addEventListener('unhandledrejection', handleUnhandledRejection)

    return () => {
      window.removeEventListener('error', handleError)
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
    }
  }, [])

  const getActivityLog = useCallback(() => {
    return [...activityRef.current]
  }, [])

  const getConsoleErrors = useCallback(() => {
    return consoleErrorsRef.current.map((e) => e.message)
  }, [])

  const getEnvironment = useCallback(() => {
    const ua = navigator.userAgent
    let browser = 'Unknown'
    if (ua.includes('Chrome') && !ua.includes('Edg'))
      browser = `Chrome ${ua.match(/Chrome\/(\S+)/)?.[1] || ''}`
    else if (ua.includes('Firefox'))
      browser = `Firefox ${ua.match(/Firefox\/(\S+)/)?.[1] || ''}`
    else if (ua.includes('Safari') && !ua.includes('Chrome'))
      browser = `Safari ${ua.match(/Version\/(\S+)/)?.[1] || ''}`
    else if (ua.includes('Edg'))
      browser = `Edge ${ua.match(/Edg\/(\S+)/)?.[1] || ''}`

    let os = 'Unknown'
    if (ua.includes('Windows')) os = 'Windows'
    else if (ua.includes('Mac')) os = 'macOS'
    else if (ua.includes('Linux')) os = 'Linux'
    else if (ua.includes('Android')) os = 'Android'
    else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS'

    return {
      browser: browser.trim(),
      os,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      currentUrl: window.location.href,
      consoleErrors: getConsoleErrors(),
    }
  }, [getConsoleErrors])

  return { addEntry, getActivityLog, getConsoleErrors, getEnvironment }
}
